import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSite } from "@/context/SiteContext";

const BASE = "https://iaajministry.org";
const ORG = "ItsAllAboutJesus Outreach and Help Center";

// Per-route titles + descriptions for clean, indexable public metadata.
const META = {
  "/": [`${ORG} | Faith in Action, People Helping People`, "A Christian outreach and help center. Request help, request prayer, or get involved serving our community in Jesus' name."],
  "/about": [`About Us | ${ORG}`, "Learn about our mission, heart, and the people behind ItsAllAboutJesus Outreach and Help Center."],
  "/announcements": [`Announcements | ${ORG}`, "The latest news, updates, and announcements from ItsAllAboutJesus Outreach and Help Center."],
  "/programs": [`Outreach & Programs | ${ORG}`, "Explore our outreach programs and the ways we serve our neighbors with compassion."],
  "/events": [`Events | ${ORG}`, "Upcoming ministry events, outreach, and opportunities to serve together."],
  "/gallery": [`Ministry in Action | ${ORG}`, "Real moments of hope, service, and love — photos, videos, and stories of hope from our community."],
  "/request-help": [`Request Help | ${ORG}`, "Need help? Submit a confidential request and our ministry team will reach out with care."],
  "/request-prayer": [`Request Prayer | ${ORG}`, "Share a prayer request with our ministry. We would be honored to pray with you."],
  "/get-involved": [`Get Involved & Volunteer | ${ORG}`, "Volunteer, donate, or partner with us. There is a place for you to serve."],
  "/contact": [`Contact Us | ${ORG}`, "Reach out to ItsAllAboutJesus Outreach and Help Center. We would love to hear from you."],
  "/schedule": [`Schedule an Appointment | ${ORG}`, "Book a time to meet or speak with our ministry team."],
};

function metaTag(key, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function linkTag(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
}

export default function Seo() {
  const { pathname } = useLocation();
  const { site } = useSite();
  useEffect(() => {
    const org = site?.contact_info?.org_name || ORG;
    const rawLogo = site?.site_settings?.logo_url || "";
    const social = site?.site_settings?.default_social_image || "";
    const chosen = social || rawLogo;
    const image = chosen.startsWith("http") ? chosen : chosen.startsWith("/api/") ? `${BASE}${chosen}` : `${BASE}/icon-512.png`;
    const base = META[pathname];
    const title = base ? base[0] : `${org} | ${site?.footer?.tagline || "Faith in Action"}`;
    const desc = base ? base[1] : (site?.footer?.tagline || "Faith in action. People helping people. Jesus at the center.");
    const url = BASE + pathname;
    const isPublic = !pathname.startsWith("/admin");

    document.title = title;
    metaTag("description", desc);
    metaTag("robots", isPublic ? "index,follow" : "noindex,nofollow");
    linkTag("canonical", url);
    metaTag("og:title", title, "property");
    metaTag("og:description", desc, "property");
    metaTag("og:url", url, "property");
    metaTag("og:site_name", org, "property");
    metaTag("og:image", image, "property");
    metaTag("twitter:title", title);
    metaTag("twitter:description", desc);
    metaTag("twitter:image", image);

    // Organization + WebSite structured data — driven by Organization Settings (SSOT).
    let ld = document.getElementById("ld-org");
    if (!ld) { ld = document.createElement("script"); ld.type = "application/ld+json"; ld.id = "ld-org"; document.head.appendChild(ld); }
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: org,
      url: BASE,
      logo: image,
      description: site?.footer?.tagline || desc,
      ...(site?.contact_info?.email ? { email: site.contact_info.email } : {}),
      ...(site?.contact_info?.phone ? { telephone: site.contact_info.phone } : {}),
      ...(site?.contact_info?.address ? { address: { "@type": "PostalAddress", streetAddress: site.contact_info.address, addressLocality: site.contact_info.city, addressRegion: site.contact_info.state, postalCode: site.contact_info.zip } } : {}),
      sameAs: Object.values(site?.social_links || {}).filter(Boolean),
    });
  }, [pathname, site]);
  return null;
}
